import { useState } from "react";
import { useSession } from "@otto/plattform";
import { assignableTo, courseOptionLabel, whyNothingAssignable } from "../lib/assign.js";
import {
  useAddCourse,
  useClassCourses,
  useCourseCatalog,
  useRemoveCourse,
} from "../lib/data.js";
import { newerVersionOf } from "../lib/settings.js";
import { ClassCourseSettings } from "./ClassCourseSettings.js";
import { NewerVersionNotice } from "./NewerVersionNotice.js";
import own from "./courses.module.css";
import styles from "../../../shared/components/shell.module.css";

/**
 * Die Lehrgänge einer Klasse: was sie führt, was dazukommen kann, was weg darf.
 *
 * Ein Aufklapper je Lehrgang: im Kopf steht, was man beim Überfliegen braucht –
 * Name, Untertitel, Fassung. Was man nur beim Einrichten braucht, liegt darunter
 * und ist zu. Bei drei Lehrgängen ist die Seite damit drei Zeilen lang statt
 * dreier Bildschirme.
 *
 * Die Fassung ist eine Anzeige. Gibt es eine neuere, steht ein Warnzeichen
 * daneben, und dahinter liegt der einzige Weg, sie zu ändern: nachziehen,
 * solange in dieser Klasse noch niemand geübt hat. Danach steht sie fest.
 */
export function ClassCourses({ classId }: { classId: string }) {
  const { client } = useSession();
  const courses = useClassCourses(client, classId);
  const catalog = useCourseCatalog(client);
  const add = useAddCourse(client, classId);
  const remove = useRemoveCourse(client, classId);
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const assigned = courses.data ?? [];
  const options = assignableTo(catalog.data ?? [], assigned);

  return (
    <div className={`${styles.card} ${styles.spaced}`}>
      <h2 className={styles.title} style={{ fontSize: "1.15rem" }}>
        Lehrgänge
      </h2>
      <p className={styles.subtitle}>
        {assigned.length > 1
          ? "Diese Klasse führt mehrere Lehrgänge. Die Kinder wählen nach der Anmeldung, womit sie üben möchten."
          : "Womit diese Klasse übt. Kommt ein zweiter Lehrgang dazu, wählen die Kinder nach der Anmeldung."}
      </p>

      {courses.isPending && <p className={styles.empty}>Wird geladen …</p>}

      <ul className={own.list}>
        {assigned.map((course) => {
          // Der letzte Lehrgang einer Klasse bleibt - die Datenbank hält das
          // ohnehin; hier steht es, damit niemand erst auf einen Knopf drückt, um
          // es zu erfahren. Ein Vorgänger schützt seinen Nachfolger NICHT: die
          // Abhängigkeit gilt dem Kind, nicht der Klassenzusammenstellung.
          const isLast = assigned.length === 1;
          const newer = newerVersionOf(course, catalog.data ?? []);
          return (
            <li key={course.curriculumId} className={own.course}>
              <details className={own.details}>
                <summary className={own.summary}>
                  <span className={own.head}>
                    <span>
                      <h3 className={own.name}>{course.title}</h3>
                      {course.subtitle && (
                        <span className={own.meta}>{course.subtitle}</span>
                      )}
                    </span>
                    <span className={own.badge}>
                      <span className={own.version}>Fassung {course.version}</span>
                      {newer !== null && (
                        <NewerVersionNotice
                          classId={classId}
                          course={course}
                          latestVersion={newer}
                          latestVersionId={
                            catalog.data?.find(
                              (o) => o.curriculumId === course.curriculumId,
                            )?.latestVersionId ?? null
                          }
                        />
                      )}
                    </span>
                  </span>
                </summary>

                <ClassCourseSettings
                  classId={classId}
                  course={course}
                  catalog={catalog.data ?? []}
                />

                <div className={own.actions}>
                  {isLast ? (
                    <p className={styles.subtitle} style={{ margin: 0 }}>
                      Der einzige Lehrgang einer Klasse lässt sich nicht entfernen.
                    </p>
                  ) : removing === course.curriculumId ? (
                    <>
                      <div
                        className={styles.notice}
                        style={{ background: "var(--warning-surface)" }}
                      >
                        Die Kinder verlieren {course.title} aus ihrer Auswahl. Die
                        Ergebnisse bleiben gespeichert, erscheinen aber nicht mehr in der
                        Übersicht.
                      </div>
                      <div className={`${styles.row} ${styles.spaced}`}>
                        <button
                          type="button"
                          className={styles.button}
                          disabled={remove.isPending}
                          onClick={() =>
                            remove.mutate(course.curriculumId, {
                              onSuccess: () => setRemoving(null),
                            })
                          }
                        >
                          {remove.isPending ? "Wird entfernt …" : "Ja, entfernen"}
                        </button>
                        <button
                          type="button"
                          className={styles.buttonQuiet}
                          onClick={() => setRemoving(null)}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.buttonQuiet}
                      onClick={() => setRemoving(course.curriculumId)}
                    >
                      {course.title} entfernen
                    </button>
                  )}
                </div>
              </details>
            </li>
          );
        })}
      </ul>

      <hr
        style={{ border: "none", borderTop: "1px solid var(--paper)", margin: "18px 0" }}
      />

      {options.length === 0 ? (
        <p className={styles.subtitle} style={{ marginBottom: 0 }}>
          {whyNothingAssignable(catalog.data ?? [], assigned)}
        </p>
      ) : (
        <>
          <label htmlFor="lehrgang-hinzufuegen">Lehrgang hinzufügen</label>
          <select
            id="lehrgang-hinzufuegen"
            className={`${styles.field} ${own.narrow}`}
            value={adding ?? ""}
            onChange={(e) => setAdding(e.target.value === "" ? null : e.target.value)}
          >
            <option value="">– bitte wählen –</option>
            {options.map((option) => (
              <option key={option.curriculumId} value={option.curriculumId}>
                {courseOptionLabel(option, catalog.data ?? [])}
              </option>
            ))}
          </select>

          {add.isError && (
            <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
              Der Lehrgang konnte nicht hinzugefügt werden.
            </div>
          )}

          {adding && (
            <button
              type="button"
              className={`${styles.button} ${styles.spaced}`}
              disabled={add.isPending}
              onClick={() => {
                const option = options.find((o) => o.curriculumId === adding);
                if (!option?.latestVersionId) return;
                add.mutate(
                  {
                    curriculumId: option.curriculumId,
                    versionId: option.latestVersionId,
                    position: assigned.length,
                  },
                  { onSuccess: () => setAdding(null) },
                );
              }}
            >
              {add.isPending ? "Wird hinzugefügt …" : "Hinzufügen"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
